import * as React from 'react';
import {
  ISelectionZoneProps,
  IObjectWithKey
} from 'office-ui-fabric-react/lib/Selection';
import { ActionBase } from '../action/Action';
import { Fragment } from '../../utilities/components/Fragment';
import { WithAction, IActionStateProps, evaluateAction } from '../withAction/WithAction';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { IComponentDecorator, IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { connectToItemCache } from './connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { ControlHandlerProvider, IControlFaceted } from './withControlHandler';
import { invokeActionControl } from '../../controls/invokeActionControl';
import { IWithSelectedItemsContextMenuProps, withSelectedItemsContextMenu } from './withSelectedItemsContextMenu';

const invocationActionKeyFacet = new AutoKeyFacet('invocationAction');

/**
 * @public
 */
export interface IInvokeActionProps {
  itemKey: string;
}

/**
 * @public
 */
export interface IConnectedToInvocationProps {
  onItemInvoked: ISelectionZoneProps['onItemInvoked'];
  onItemContextMenu: ISelectionZoneProps['onItemContextMenu'];
  InvokeAction: React.ComponentType<IInvokeActionProps>;
}

interface IActionManagerInternalState {
  actionsByItemKey: {
    [itemKey: string]: {
      [actionKey: string]: boolean;
    } | undefined;
  };
  actionsByActionKey: {
    [actionKey: string]: IActionStateProps & { itemKey: string; } | undefined
  };
}

/**
 * @public
 */
export type IDecorateConnectToInvocationActionProps<TProps extends TInjectedProps, TInjectedProps, TOwnProps> =
  IDecoratedProps<TProps, TInjectedProps, TOwnProps>;

/**
 * @internal
 * @deprecated Use `withItemSetSelection` and `withSelectedItemsContextMenu` in combination instead.
 */
export function connectInvocationAction<TInjectedProps, TOwnProps>(
  InvocationAction: React.ComponentType<IInvokeActionProps>,
  mapToProps: (invocationProps: IConnectedToInvocationProps, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, TInjectedProps, TOwnProps>> => {
    type IProps = IDecoratedProps<P, TInjectedProps, TOwnProps>;

    const Component = component;

    class ActionManager extends React.PureComponent<IProps & IWithSelectedItemsContextMenuProps> {
      private _invokeActionType: React.ComponentType<{ itemKey: string; }>;

      /**
       * Track 'internal' state, which should notr tirgger re-renders when updated.
       */
      private _state: IActionManagerInternalState;

      private _controlHandlers: IControlFaceted;

      constructor(props: IProps) {
        super(props);

        this._invokeActionType = createInvokeActionType({
          onRemove: this._onRemove,
          onUpdate: this._onUpdate,
          component: InvocationAction
        });

        this._state = {
          actionsByActionKey: {},
          actionsByItemKey: {}
        };

        this._controlHandlers = invokeActionControl.pack(this._invokeActionControlHandler);
      }

      public render(): JSX.Element {
        return (
          <Fragment>
            <ControlHandlerProvider
              controlHandlers={ this._controlHandlers }
            >
              <Component
                { ...this.props }
                { ...mapToProps({
                  InvokeAction: this._invokeActionType,
                  onItemInvoked: this._onItemInvoked,
                  onItemContextMenu: this.props.onItemContextMenu
                }, this.props as Readonly<TOwnProps>) }
              />
            </ControlHandlerProvider>
          </Fragment>
        );
      }

      private _invokeActionControlHandler = (
        itemCache: IItemCache,
        props: typeof invokeActionControl.inputType
      ): typeof invokeActionControl.returnType => {
        const { itemKey } = props;

        const InvokeAction = this._invokeActionType;

        if (!props.action) {
          return {
            action: evaluateAction(itemCache, (
              <InvokeAction itemKey={ itemKey } />
            ))
          };
        }

        return {};
      }

      private _onItemInvoked = (item: IObjectWithKey | undefined): void => {
        if (!item || typeof item.key !== 'string') {
          return;
        }

        const {
          actionsByActionKey,
          actionsByItemKey
        } = this._state;

        const actionsByKey = actionsByItemKey[item.key];

        if (actionsByKey) {
          for (const actionKey in actionsByKey) {
            if (Object.prototype.hasOwnProperty.call(actionsByKey, actionKey)) {
              const actionState = actionsByActionKey[actionKey];

              if (actionState && actionState.isAvailable) {
                actionState.execute().catch(() => {
                  // Ignore the error since there is nowhere to put it.
                });
              }
            }
          }
        }
      }

      private _onUpdate = (actionKey: string, options: IActionStateProps & { itemKey: string; }): void => {
        const actionsByActionKey = this._state.actionsByActionKey;
        const actionsByItemKey = this._state.actionsByItemKey;

        const previousActionState = actionsByActionKey[actionKey];

        if (previousActionState && options.itemKey !== previousActionState.itemKey) {
          const itemState = actionsByItemKey[previousActionState.itemKey];

          if (itemState) {
            if (Object.keys(itemState).length > 1) {
              delete itemState[actionKey];
            } else {
              delete actionsByItemKey[previousActionState.itemKey];
            }
          }
        }

        actionsByActionKey[actionKey] = options;

        {
          const itemState = actionsByItemKey[options.itemKey] || (actionsByItemKey[options.itemKey] = {});

          itemState[actionKey] = true;
        }
      }

      private _onRemove = (actionKey: string): void => {
        const actionsByActionKey = this._state.actionsByActionKey;
        const actionsByItemKey = this._state.actionsByItemKey;

        const previousActionState = actionsByActionKey[actionKey];

        if (previousActionState) {
          const itemState = actionsByItemKey[previousActionState.itemKey];

          if (itemState) {
            if (Object.keys(itemState).length > 1) {
              delete itemState[actionKey];
            } else {
              delete actionsByItemKey[previousActionState.itemKey];
            }
          }
        }

        delete actionsByActionKey[actionKey];
      }
    }

    return withDisplayName('ConnectToInvocationAction')(
      withSelectedItemsContextMenu()(ActionManager) as {} as React.ComponentType<IDecoratedProps<P, TInjectedProps, TOwnProps>>
    );
  };
}

function createInvokeActionType(coreProps: {
  onUpdate: (actionKey: string, options: IActionStateProps & { itemKey: string; }) => void;
  onRemove: (actionKey: string) => void;
  component: React.ComponentType<IInvokeActionProps>;
}): React.ComponentType<IInvokeActionProps> {
  const Component = coreProps.component;

  class InvokeAction extends React.PureComponent<{
    itemKey: string;
    action: JSX.Element | null;
  }> {
    private _actionKey: string;
    private _disposed?: boolean;

    constructor(props: {
      itemKey: string;
      action: JSX.Element | null;
    }) {
      super(props);

      this._actionKey = invocationActionKeyFacet.serializeNext();
    }

    public render(): JSX.Element {
      return (
        <WithAction
          action={ this.props.action }
          onActionUpdated={ this._onActionUpdated }
        >
          { ({ isAvailable, execute: onExecute, isToggled }: IActionStateProps) => {
            return (
              <ActionBase
                isAvailable={ isAvailable }
                isToggled={ isToggled }
                onExecute={ onExecute }
              />
            );
          } }
        </WithAction>
      );
    }

    public componentWillUnmount(): void {
      this._disposed = true;
      coreProps.onRemove(this._actionKey);
    }

    private _onActionUpdated = (actionState: IActionStateProps): void => {
      if (this._disposed) {
        return;
      }

      coreProps.onUpdate(this._actionKey, {
        ...actionState,
        itemKey: this.props.itemKey
      });
    }
  }

  return withDisplayName('InvokeAction')(connectToItemCache((itemCache: IItemCache, props: {
    itemKey: string;
  }): {
      action: JSX.Element | null;
    } => {
    const action = evaluateAction(itemCache, (
      <Component { ...props } />
    ));

    return {
      action
    };
  })(InvokeAction));
}
